import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';
import { cataloguePdfFile, CATALOGUE_LINKS, CATALOGUE_STORE, catalogueLogo, setSize, MAX_PRODUCT_IMAGES } from '../../utils/cataloguePdf';

const toImageUrl = (img) => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  return img.url || '';
};

const formatPrice = (price) => {
  if (price == null || price === '' || Number(price) === 0) return '';
  return `₹${Number(price).toLocaleString('en-IN')}`;
};

const WhatsAppIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.5 3.5A11 11 0 0 0 2.1 17.7L1 23l5.4-1.1A11 11 0 0 0 12 23a11 11 0 0 0 8.5-19.5zM12 21.2a9.2 9.2 0 0 1-4.7-1.3l-.3-.2-3.2.7.7-3.1-.2-.3A9.2 9.2 0 1 1 12 21.2zm5.3-6.9c-.3-.1-1.7-.8-2-.9s-.5-.1-.6.1-.7.9-.9 1.1-.3.2-.6.1a7.5 7.5 0 0 1-2.2-1.4 8.3 8.3 0 0 1-1.5-1.9c-.2-.3 0-.4.1-.6l.4-.5.2-.3c.1-.1 0-.3 0-.4l-.9-2.1c-.2-.5-.5-.4-.6-.4h-.5c-.2 0-.4.1-.6.3s-.8.8-.8 1.9.8 2.2.9 2.3c.1.2 1.6 2.5 3.8 3.5 1.4.6 1.9.6 2.6.5.4-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3 0-.1-.2-.2-.5-.3z" />
  </svg>
);

const downloadFile = (file) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const CataloguePage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    fetch(getApiUrl('/api/admin/catalogues'))
      .then(res => res.json())
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => setError('Failed to load catalogue'))
      .finally(() => setLoading(false));
  }, []);

  const makePdf = async () => cataloguePdfFile(items);

  const handleDownload = async () => {
    setBusy('download');
    try {
      const file = await makePdf();
      downloadFile(file);
    } catch (err) {
      console.error(err);
      alert('Could not create PDF. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const handleWhatsAppShare = async () => {
    setBusy('whatsapp');
    try {
      const file = await makePdf();
      const shareData = {
        title: 'theVastra.store Catalogue',
        text: 'theVastra.store Lookbook PDF',
        files: [file]
      };

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share(shareData);
        return;
      }

      downloadFile(file);
      const message = encodeURIComponent(
        'Hi! Sharing theVastra.store catalogue PDF. I have downloaded theVastra.store-Catalogue.pdf — please attach it here to send.'
      );
      window.open(`https://wa.me/?text=${message}`, '_blank', 'noopener,noreferrer');
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error(err);
      alert('Could not share PDF. Please try downloading it instead.');
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-500">Loading catalogue...</div>;
  }

  if (error) {
    return <div className="min-h-[60vh] flex items-center justify-center text-red-500">{error}</div>;
  }

  return (
    <div className="catalogue-preview bg-[#cfc8c0] min-h-screen py-6 px-3 sm:px-6">
      <Helmet>
        <title>Catalogue | theVastra.store</title>
      </Helmet>

      <div className="max-w-3xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#56112a]">Catalogue</h1>
          <p className="text-sm text-gray-700">All products in one PDF lookbook</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleDownload}
            disabled={!!busy}
            className="inline-flex items-center justify-center bg-white border border-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-60"
          >
            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
            {busy === 'download' ? 'Preparing PDF…' : 'Save PDF'}
          </button>
          <button
            onClick={handleWhatsAppShare}
            disabled={!!busy}
            className="inline-flex items-center justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white px-4 py-2 rounded-lg disabled:opacity-60"
          >
            <WhatsAppIcon />
            {busy === 'whatsapp' ? 'Preparing PDF…' : 'Share on WhatsApp'}
          </button>
        </div>
      </div>

      <div className="catalogue-stack max-w-[720px] mx-auto space-y-6">
          <section className="catalogue-sheet bg-[#f7e4e8] shadow-xl overflow-hidden">
            <div className="bg-[#fff8f6] m-3 sm:m-5 rounded-2xl overflow-hidden">
              <div className="px-5 sm:px-10 pt-8 pb-6 text-center">
                <div className="mx-auto w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden">
                  <img src={catalogueLogo} alt={CATALOGUE_STORE.name} className="w-full h-full object-cover scale-[1.06]" />
                </div>
                <p className="italic text-[#801c33] text-lg mt-6">Welcome to</p>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#801c33] leading-tight">{CATALOGUE_STORE.name}</h2>
                <p className="text-[#5a2837] mt-1">Where Tradition Meets Style</p>
                <div className="w-20 h-px bg-[#801c33]/20 mx-auto my-4" />
                <p className="text-gray-600 text-sm sm:text-base">
                  Your one-stop destination for Ethnic, Western & Daily Wear.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                  {[
                    {
                      label: 'Store address',
                      value: CATALOGUE_STORE.addressLines.map(line => (
                        <span key={line} className="block">{line}</span>
                      )),
                      url: CATALOGUE_LINKS.maps
                    },
                    { label: 'Call / WhatsApp', value: CATALOGUE_STORE.phone, url: CATALOGUE_LINKS.whatsapp },
                    { label: 'Email', value: CATALOGUE_STORE.email, url: CATALOGUE_LINKS.email },
                    { label: 'Instagram', value: CATALOGUE_STORE.instagramHandle, url: CATALOGUE_LINKS.instagram },
                    { label: 'Facebook', value: CATALOGUE_STORE.name, url: CATALOGUE_LINKS.facebook },
                    { label: 'Website', value: CATALOGUE_STORE.websiteLabel, url: CATALOGUE_LINKS.website }
                  ].map((tile) => (
                    <a
                      key={tile.label}
                      href={tile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-xl px-4 py-3 shadow-sm hover:shadow-md hover:bg-[#fff2f5] transition"
                    >
                      <span className="block uppercase tracking-wider text-[10px] text-gray-400">{tile.label}</span>
                      <span className="block font-semibold text-[#801c33] text-sm break-words">{tile.value}</span>
                    </a>
                  ))}
                </div>
              </div>
              <div className="bg-[#801c33] text-[#f7e4e8] flex justify-around py-3 px-4 text-[11px] sm:text-xs font-bold tracking-[0.2em]">
                <span>QUALITY</span>
                <span>STYLE</span>
                <span>TRUST</span>
              </div>
            </div>
          </section>

          {items.length === 0 && (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Add catalogue products in the admin panel to include them in this lookbook.
            </div>
          )}

          {items.map((item) => {
            const photos = (item.images || []).slice(0, MAX_PRODUCT_IMAGES).map(toImageUrl).filter(Boolean);
            return (
              <section key={item._id} className="catalogue-sheet bg-white shadow-xl overflow-hidden flex flex-col">
                {photos.length > 0 ? (
                  <div
                    className="w-full bg-gray-100 p-2 grid gap-2"
                    style={{ gridTemplateColumns: `repeat(${photos.length}, minmax(0, 1fr))` }}
                  >
                    {photos.map((url, idx) => (
                      <div key={idx} className="bg-white rounded overflow-hidden aspect-[3/4]">
                        <img
                          src={url}
                          alt={photos.length > 1 ? `${item.title} view ${idx + 1}` : item.title}
                          className="w-full h-full object-contain"
                          crossOrigin="anonymous"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="w-full aspect-[4/3] bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                    No image
                  </div>
                )}
                <div className="p-5 sm:p-8 flex-1 flex flex-col">
                  <h2 className="text-2xl sm:text-3xl font-serif text-[#56112a] mb-2">{item.title}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mb-4">
                    {[
                      {
                        label: 'Retail price',
                        value: formatPrice(item.retailPrice ?? item.price),
                        valueClass: 'text-pink-600',
                        note: 'Per piece'
                      },
                      {
                        label: 'Wholesale price',
                        value: formatPrice(item.wholesalePrice),
                        valueClass: 'text-[#801c33]',
                        note: setSize(item)
                          ? `Per set · ${setSize(item)} pcs (${(item.sizes || []).join(', ')})`
                          : 'Per set'
                      }
                    ]
                      .filter(price => price.value)
                      .map(price => (
                        <div key={price.label} className="rounded-xl bg-[#f7e4e8] px-4 py-3">
                          <p className="uppercase tracking-wider text-[11px] font-semibold text-[#801c33]/60">{price.label}</p>
                          <p className={`text-2xl font-bold leading-tight ${price.valueClass}`}>{price.value}</p>
                          <p className="text-[11px] text-[#801c33]/70 mt-0.5">{price.note}</p>
                        </div>
                      ))}
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-5">{item.description}</p>
                  <div className="grid grid-cols-2 gap-4 text-sm border-t border-gray-200 pt-4 mt-auto">
                    <div>
                      <p className="uppercase tracking-wider text-[11px] text-gray-400 mb-1">Sizes</p>
                      <p className="font-medium text-gray-800">{(item.sizes || []).join(' · ') || '—'}</p>
                    </div>
                    <div>
                      <p className="uppercase tracking-wider text-[11px] text-gray-400 mb-1">Colours</p>
                      <p className="font-medium text-gray-800">{(item.colors || []).join(' · ') || '—'}</p>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
    </div>
  );
};

export default CataloguePage;
